import * as XLSX from "xlsx";

// ---- helpers ----

type Row = (string | number | null | undefined)[];

function amt(v: number | null | undefined): number | string {
    if (v == null) return "";
    return Math.round(v * 100) / 100;
}

function download(wb: XLSX.WorkBook, filename: string) {
    XLSX.writeFile(wb, filename);
}

/** dd/mm/aaaa de una fecha ISO (aaaa-mm-dd), sin pasar por Date para no moverla de día. */
function fmtDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const [y, m, d] = String(iso).slice(0, 10).split("-");
    return d && m && y ? `${d}/${m}/${y}` : String(iso);
}

/** Texto del periodo de un informe (#14733): el año entero o «del … al …». */
function periodLabel(year: number | undefined, from?: string | null, to?: string | null): string {
    const full = year != null && (!from || from.endsWith("-01-01")) && (!to || to.endsWith("-12-31"));
    if (full) return `${year}`;
    return `del ${fmtDate(from ?? `${year}-01-01`)} al ${fmtDate(to ?? `${year}-12-31`)}`;
}

/** Sufijo del nombre de fichero para el periodo: el año o «aaaa-mm-dd-a-aaaa-mm-dd». */
function periodSlug(year: number | undefined, from?: string | null, to?: string | null): string {
    const full = year != null && (!from || from.endsWith("-01-01")) && (!to || to.endsWith("-12-31"));
    return full ? `${year}` : `${from ?? `${year}-01-01`}-a-${to ?? `${year}-12-31`}`;
}

function bold(ws: XLSX.WorkSheet, rowIndex: number, colCount: number) {
    for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({r: rowIndex, c});
        if (!ws[addr]) ws[addr] = {v: "", t: "s"};
        ws[addr].s = {font: {bold: true}};
    }
}

// ---- Trial Balance ----

export function exportTrialBalanceXls(report: any, year: number) {
    const headerRowIdx = 2;
    const rows: Row[] = [
        [`Balance de Sumas y Saldos — ${periodLabel(year, report.dateFrom, report.dateTo)}`],
        [],
        ["Código", "Denominación", "Debe", "Haber", "Saldo deudor", "Saldo acreedor", "Saldo"],
    ];
    for (const line of report.lines ?? []) {
        const saldo = (line.debitBalance ?? 0) - (line.creditBalance ?? 0);
        rows.push([
            line.accountCode,
            line.accountName,
            amt(line.totalDebit),
            amt(line.totalCredit),
            (line.debitBalance ?? 0) > 0 ? amt(line.debitBalance) : "",
            (line.creditBalance ?? 0) > 0 ? amt(line.creditBalance) : "",
            amt(saldo),
        ]);
    }
    const totalsRowIdx = rows.length;
    rows.push(["", "TOTALES", amt(report.totalDebit), amt(report.totalCredit), amt(report.totalDebitBalance), amt(report.totalCreditBalance), amt(0)]);

    const diff = (report.totalDebitBalance ?? 0) - (report.totalCreditBalance ?? 0);
    const balanced = Math.abs(diff) < 0.01;
    rows.push([balanced ? "✓ Saldo = 0 — balance cuadrado" : "✗ DESCUADRE — saldo ≠ 0", "", "", "", "", amt(diff), ""]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{wch: 12}, {wch: 42}, {wch: 16}, {wch: 16}, {wch: 16}, {wch: 16}, {wch: 16}];
    bold(ws, headerRowIdx, 7);
    bold(ws, totalsRowIdx, 7);
    XLSX.utils.book_append_sheet(wb, ws, "Sumas y Saldos");
    download(wb, `sumas-saldos-${periodSlug(year, report.dateFrom, report.dateTo)}.xlsx`);
}

// ---- Ledger ----

/** Filas de un mayor: saldo anterior, movimientos (con la subcuenta si es una agrupadora) y totales (#14733). */
function ledgerRows(report: any): Row[] {
    const rows: Row[] = [];
    rows.push(["", "", "Saldo anterior", "", "", "", "", amt(report.openingBalance ?? 0)]);
    for (const e of report.entries ?? []) {
        rows.push([
            fmtDate(e.entryDate),
            e.entryNumber,
            e.accountCode && e.accountCode !== report.accountCode ? e.accountCode : "",
            e.description,
            e.documentNumber ?? "",
            e.debit > 0 ? amt(e.debit) : "",
            e.credit > 0 ? amt(e.credit) : "",
            amt(e.runningBalance),
        ]);
    }
    rows.push(["", "", "", "Totales y saldo final", "", amt(report.totalDebit), amt(report.totalCredit), amt(report.finalBalance)]);
    return rows;
}

const LEDGER_HEADER = ["Fecha", "Nº asiento", "Subcuenta", "Descripción", "Doc.", "Debe", "Haber", "Saldo"];
const LEDGER_COLS = [{wch: 11}, {wch: 10}, {wch: 11}, {wch: 44}, {wch: 14}, {wch: 14}, {wch: 14}, {wch: 14}];

export function exportLedgerXls(report: any, year: number) {
    const period = periodLabel(year, report.dateFrom, report.dateTo);
    const rows: Row[] = [
        [`Libro Mayor — ${report.accountCode} ${report.accountName} — ${period}`],
        ["Nº asiento: el del Libro Diario."],
        LEDGER_HEADER,
        ...ledgerRows(report),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = LEDGER_COLS;
    bold(ws, 2, LEDGER_HEADER.length);
    bold(ws, rows.length - 1, LEDGER_HEADER.length);
    XLSX.utils.book_append_sheet(wb, ws, "Libro Mayor");
    download(wb, `libro-mayor-${report.accountCode}-${periodSlug(year, report.dateFrom, report.dateTo)}.xlsx`);
}

/** Mayor de todas las cuentas con movimiento (#14733): una hoja con un bloque por cuenta. */
export function exportGeneralLedgerXls(report: any) {
    const rows: Row[] = [
        [`Libro Mayor — del ${fmtDate(report.dateFrom)} al ${fmtDate(report.dateTo)}`],
        ["Todas las cuentas con movimiento en el periodo. Nº asiento: el del Libro Diario."],
        [],
    ];
    const boldRows: number[] = [];
    for (const acc of report.accounts ?? []) {
        boldRows.push(rows.length);
        rows.push([acc.accountCode, acc.accountName]);
        boldRows.push(rows.length);
        rows.push(LEDGER_HEADER);
        const block = ledgerRows(acc);
        rows.push(...block);
        boldRows.push(rows.length - 1);
        rows.push([]);
    }
    boldRows.push(rows.length);
    rows.push(["", "", "", "Total del periodo", "", amt(report.totalDebit), amt(report.totalCredit), ""]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = LEDGER_COLS;
    boldRows.forEach(r => bold(ws, r, LEDGER_HEADER.length));
    XLSX.utils.book_append_sheet(wb, ws, "Libro Mayor");
    download(wb, `libro-mayor-${report.dateFrom}-a-${report.dateTo}.xlsx`);
}

// ---- Libro Diario (#14733) ----

/** Libro Diario de un periodo: un apunte por fila, con el número de asiento del PDF que se legaliza. */
export function exportJournalXls(report: any) {
    const header = ["Nº asiento", "Fecha", "Descripción del asiento", "Cuenta", "Título de la cuenta", "Concepto", "Debe", "Haber"];
    const rows: Row[] = [
        [`Libro Diario — del ${fmtDate(report.dateFrom)} al ${fmtDate(report.dateTo)}`],
        ["Numeración del Libro Diario del ejercicio (la del PDF que se legaliza y la del mayor)."],
        header,
    ];
    for (const e of report.entries ?? []) {
        for (const l of e.lines ?? []) {
            rows.push([e.number, fmtDate(e.date), e.description ?? "", l.accountCode, l.accountName ?? "", l.description ?? "",
                l.debit > 0 ? amt(l.debit) : "", l.credit > 0 ? amt(l.credit) : ""]);
        }
    }
    rows.push([]);
    const totalsRowIdx = rows.length;
    rows.push(["", "", `Total (${(report.entries ?? []).length} asientos)`, "", "", "", amt(report.totalDebit), amt(report.totalCredit)]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{wch: 10}, {wch: 11}, {wch: 40}, {wch: 11}, {wch: 34}, {wch: 40}, {wch: 14}, {wch: 14}];
    bold(ws, 2, header.length);
    bold(ws, totalsRowIdx, header.length);
    XLSX.utils.book_append_sheet(wb, ws, "Libro Diario");
    download(wb, `libro-diario-${report.dateFrom}-a-${report.dateTo}.xlsx`);
}

// ---- PyG ----

type PyGItem = {label: string; value?: number | null; bold?: boolean; divider?: boolean};

function pygRows(pl: any): PyGItem[] {
    return [
        {label: `Cuenta de Pérdidas y Ganancias ${periodLabel(pl.periodYear, pl.dateFrom, pl.dateTo)} — modelo abreviado PGCE`, bold: true},
        {label: ""},
        {label: "1. Importe neto de la cifra de negocios", value: pl.cifraNegocios},
        {label: "2. Variación de existencias de productos terminados y en curso", value: pl.variacionExistenciasProductos},
        {label: "3. Aprovisionamientos", value: pl.aprovisionamientos},
        {label: "4. Otros ingresos de explotación", value: pl.otrosIngresosExplotacion},
        {label: "5. Gastos de personal", value: pl.gastosPersonal},
        {label: "6. Otros gastos de explotación", value: pl.otrosGastosExplotacion},
        {label: "7. Amortización del inmovilizado", value: pl.amortizacion},
        {label: "8. Imputación de subvenciones de inmovilizado no financiero y otras", value: pl.imputacionSubvenciones},
        {label: "9. Excesos de provisiones", value: pl.excesosProvisiones},
        {label: "10. Deterioro y resultado por enajenaciones del inmovilizado", value: pl.deterioroYEnajenaciones},
        {label: "11. Otros resultados", value: pl.otrosResultados},
        ...(Number(pl.sinClasificar ?? 0) !== 0
            ? [{label: `Sin partida del modelo (${(pl.cuentasSinClasificar ?? []).join(", ")})`, value: pl.sinClasificar}]
            : []),
        {label: "A1) RESULTADO DE EXPLOTACIÓN", value: pl.resultadoExplotacion, bold: true, divider: true},
        {label: ""},
        {label: "12. Ingresos financieros", value: pl.ingresosFinancieros},
        {label: "13. Gastos financieros", value: pl.gastosFinancieros},
        {label: "14. Variación de valor razonable en instrumentos financieros", value: pl.variacionValorRazonable},
        {label: "15. Diferencias de cambio", value: pl.diferenciasCambio},
        {label: "16. Deterioro y resultado por enajenaciones de instrumentos financieros", value: pl.deterioroInstrFinancieros},
        {label: "A2) RESULTADO FINANCIERO", value: pl.resultadoFinanciero, bold: true, divider: true},
        {label: ""},
        {label: "A3) RESULTADO ANTES DE IMPUESTOS", value: pl.resultadoAntesImpuestos, bold: true, divider: true},
        {label: ""},
        {label: "17. Impuesto sobre beneficios", value: pl.impuestoBeneficios},
        {label: "A4) RESULTADO DEL EJERCICIO", value: pl.resultadoEjercicio, bold: true, divider: true},
    ];
}

export function exportProfitLossXls(pl: any) {
    const items = pygRows(pl);
    const rows: Row[] = items.map(item => [item.label, item.value != null ? amt(item.value) : ""]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{wch: 65}, {wch: 16}];

    // Bold rows
    items.forEach((item, idx) => {
        if (item.bold) bold(ws, idx, 2);
    });

    XLSX.utils.book_append_sheet(wb, ws, "PyG");
    download(wb, `perdidas-ganancias-${periodSlug(pl.periodYear, pl.dateFrom, pl.dateTo)}.xlsx`);
}

// ---- Balance de Situación ----

function sectionRows(section: any): Row[] {
    if (!section) return [];
    const rows: Row[] = [];
    rows.push([`${section.code}) ${section.label}`]);
    for (const line of section.lines ?? []) {
        rows.push([`  ${line.code}`, line.label, amt(line.amount)]);
    }
    rows.push([`Total ${section.label}`, "", amt(section.total)]);
    rows.push([""]);
    return rows;
}

function bsDate(bs: any): string {
    return `a ${fmtDate(bs.dateTo ?? `${bs.periodYear}-12-31`)}`;
}

export function exportBalanceSheetXls(bs: any) {
    const wb = XLSX.utils.book_new();

    // --- Activo sheet ---
    const activoRows: Row[] = [
        [`Balance de Situación ${bsDate(bs)} — modelo abreviado PGCE`],
        [],
        ["ACTIVO"],
        [],
        ...sectionRows(bs.activoNoCorriente),
        ...sectionRows(bs.activoCorriente),
        ["TOTAL ACTIVO", "", amt(bs.totalActivo)],
    ];
    const wsA = XLSX.utils.aoa_to_sheet(activoRows);
    wsA["!cols"] = [{wch: 12}, {wch: 52}, {wch: 16}];
    XLSX.utils.book_append_sheet(wb, wsA, "Activo");

    // --- Patrimonio y Pasivo sheet ---
    const pasivoRows: Row[] = [
        [`Balance de Situación ${bsDate(bs)} — modelo abreviado PGCE`],
        [],
        ["PATRIMONIO NETO Y PASIVO"],
        [],
        ...sectionRows(bs.patrimonioNeto),
        ...sectionRows(bs.pasivoNoCorriente),
        ...sectionRows(bs.pasivoCorriente),
        ["TOTAL PATRIMONIO NETO Y PASIVO", "", amt(bs.totalPatrimonioNetoYPasivo)],
    ];
    const wsP = XLSX.utils.aoa_to_sheet(pasivoRows);
    wsP["!cols"] = [{wch: 12}, {wch: 52}, {wch: 16}];
    XLSX.utils.book_append_sheet(wb, wsP, "Patrimonio y Pasivo");

    download(wb, `balance-situacion-${bs.dateTo ?? bs.periodYear}.xlsx`);
}

// ---- Dedicación de la plantilla (#14080) ----

/**
 * Cuadre de dedicación para la auditoría de bonificaciones: una hoja con el cuadre por persona y mes,
 * otra con cada imputación de Redmine y, si las hay, las horas que no se atribuyen a nadie.
 */
export function exportDedicationAuditXls(report: any, entries: any[]) {
    const wb = XLSX.utils.book_new();

    const summary: Row[] = [
        [`Dedicación de la plantilla — ${report.from} a ${report.to}`],
        [`Horas imputadas en Redmine (todos los proyectos) frente a la jornada del calendario. Generado el ${report.generatedOn}.`],
        [],
        ["Persona", "NIF", "Bonificable I+D+i", "Mes", "Laborables", "Festivos", "Horas de contrato",
            "Horas de ausencia", "Ausencias (días)", "Previstas", "Imputadas", "Diferencia",
            "Imputadas en días sin jornada", "Ausencias sin aprobar", "Aviso"],
    ];
    for (const p of report.people ?? []) {
        for (const m of p.months ?? []) {
            summary.push([
                p.personName, p.nif ?? "", p.rebate ? "Sí" : "No", m.month, m.workingDays, m.holidayDays,
                amt(m.contractHours), amt(m.absenceHours),
                Object.entries(m.absences ?? {}).map(([k, v]) => `${k}: ${v}`).join("; "),
                amt(m.expectedHours), amt(m.imputedHours), amt(m.difference), amt(m.hoursOnDaysOff),
                (m.pendingAbsences ?? []).join("; "), m.warning ?? "",
            ]);
        }
    }
    const ws1 = XLSX.utils.aoa_to_sheet(summary);
    ws1["!cols"] = [{wch: 32}, {wch: 12}, {wch: 10}, {wch: 9}, {wch: 10}, {wch: 9}, {wch: 10}, {wch: 10},
        {wch: 36}, {wch: 10}, {wch: 10}, {wch: 10}, {wch: 12}, {wch: 36}, {wch: 40}];
    bold(ws1, 3, 15);
    XLSX.utils.book_append_sheet(wb, ws1, "Cuadre");

    const detail: Row[] = [["Persona", "Fecha", "Proyecto", "Tarea", "Título de la tarea", "Actividad", "Horas", "Comentario"]];
    for (const e of entries) {
        detail.push([e.personName, e.date, e.project ?? "", e.issueId ?? "", e.issueSubject ?? "", e.activity ?? "",
            amt(e.hours), e.comment ?? ""]);
    }
    const ws2 = XLSX.utils.aoa_to_sheet(detail);
    ws2["!cols"] = [{wch: 32}, {wch: 11}, {wch: 36}, {wch: 8}, {wch: 50}, {wch: 14}, {wch: 8}, {wch: 50}];
    bold(ws2, 0, 8);
    XLSX.utils.book_append_sheet(wb, ws2, "Imputaciones");

    if ((report.unmatched ?? []).length > 0) {
        const unmatched: Row[] = [["Usuario de Redmine", "Correo", "Motivo", "Horas"]];
        for (const u of report.unmatched) {
            unmatched.push([u.user, u.mail ?? "", u.reason, amt(u.hours)]);
        }
        const ws3 = XLSX.utils.aoa_to_sheet(unmatched);
        ws3["!cols"] = [{wch: 24}, {wch: 36}, {wch: 40}, {wch: 8}];
        bold(ws3, 0, 4);
        XLSX.utils.book_append_sheet(wb, ws3, "Sin atribuir");
    }

    download(wb, `dedicacion-${report.from}-a-${report.to}.xlsx`);
}

// ---- Libros registro de IVA (#14730) ----

const BOX_LABEL: Record<string, string> = {
    "07/09": "07/09 Régimen general 21 %",
    "04/06": "04/06 Régimen general 10 %",
    "01/03": "01/03 Régimen general 4 %",
    "14/15": "14/15 Modificación de bases y cuotas",
    "10/11": "10/11 Adquisiciones intracomunitarias",
    "12/13": "12/13 Otras inversiones del sujeto pasivo",
    "28/29": "28/29 Operaciones interiores corrientes",
    "36/37": "36/37 Adquisiciones intracomunitarias corrientes",
    "40/41": "40/41 Rectificación de deducciones",
};

const SOURCE_LABEL: Record<string, string> = {MODULO: "Factura recibida", MANUAL: "Asiento manual", CONTASOL: "Asiento de Contasol"};

function avisosRows(avisos: string[] | undefined): Row[] {
    return (avisos ?? []).length > 0 ? [[], ["Avisos"], ...(avisos ?? []).map(a => [a] as Row)] : [];
}

/**
 * Libro registro de facturas expedidas (art. 63 RIVA) de un trimestre: una fila por factura y tipo de IVA.
 */
export function exportVatRegisterIssuedXls(reg: any) {
    const header = ["Ejercicio", "Periodo", "Tipo de factura", "Fecha expedición", "Fecha operación", "Serie", "Número",
        "Factura rectificada", "NIF destinatario", "País", "Nombre destinatario", "Clave régimen", "Calificación",
        "Base imponible", "Tipo IVA %", "Cuota IVA repercutida", "Total factura", "Casillas 303"];
    const rows: Row[] = [
        [`Libro registro de facturas expedidas — ${reg.quarter}T ${reg.year}`],
        [],
        header,
    ];
    for (const l of reg.lines ?? []) {
        rows.push([reg.year, `${reg.quarter}T`, l.invoiceType ?? "", l.issueDate ?? "", l.operationDate ?? "", l.series ?? "",
            l.number ?? "", l.rectifiedInvoice ?? "", l.customerNif ?? "", l.customerCountry ?? "", l.customerName ?? "",
            l.regimeKey ?? "", l.qualification ?? "", amt(l.base), amt(l.rate), amt(l.vat), amt(l.total),
            BOX_LABEL[l.box303] ?? l.box303 ?? ""]);
    }
    rows.push([]);
    const totalsRowIdx = rows.length;
    rows.push(["", "", "", "", "", "", "", "", "", "", "TOTAL", "", "", amt(reg.totalBase), "", amt(reg.totalVat), amt(reg.total), ""]);
    rows.push(...avisosRows(reg.avisos));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{wch: 8}, {wch: 7}, {wch: 7}, {wch: 11}, {wch: 11}, {wch: 6}, {wch: 8}, {wch: 10}, {wch: 12}, {wch: 5},
        {wch: 40}, {wch: 8}, {wch: 8}, {wch: 14}, {wch: 8}, {wch: 14}, {wch: 14}, {wch: 30}];
    bold(ws, 2, header.length);
    bold(ws, totalsRowIdx, header.length);
    XLSX.utils.book_append_sheet(wb, ws, `Expedidas ${reg.quarter}T`);
    download(wb, `libro-iva-expedidas-${reg.year}-${reg.quarter}T.xlsx`);
}

/**
 * Libro registro de facturas recibidas (art. 64 RIVA) de un trimestre: facturas del módulo y asientos del diario
 * con IVA soportado, con la inversión del sujeto pasivo marcada y la casilla del 303 de cada una.
 */
export function exportVatRegisterReceivedXls(reg: any) {
    const header = ["Ejercicio", "Periodo", "Origen", "Fecha registro", "Nº asiento", "Fecha expedición",
        "Nº factura / concepto", "NIF expedidor", "Nombre expedidor", "Cuenta proveedor", "Territorio",
        "Inversión sujeto pasivo", "Base imponible", "Tipo IVA %", "Cuota soportada", "Cuota deducible",
        "Cuota autorrepercutida", "Casillas 303 devengado", "Casillas 303 deducible", "Retención", "Total factura"];
    const rows: Row[] = [
        [`Libro registro de facturas recibidas — ${reg.quarter}T ${reg.year}`],
        [],
        header,
    ];
    for (const l of reg.lines ?? []) {
        rows.push([reg.year, `${reg.quarter}T`, SOURCE_LABEL[l.source] ?? l.source ?? "", l.registrationDate ?? "",
            l.journalEntryNumber ?? "", l.issueDate ?? "", l.invoiceNumber ?? "", l.supplierNif ?? "", l.supplierName ?? "",
            l.supplierAccount ?? "", l.supplierTerritory ?? "", l.reverseCharge ? "Sí" : "No", amt(l.base), amt(l.rate),
            amt(l.vat), amt(l.deductibleVat), l.reverseCharge ? amt(l.selfChargedVat) : "",
            BOX_LABEL[l.reverseChargeBox303] ?? l.reverseChargeBox303 ?? "", BOX_LABEL[l.deductibleBox303] ?? l.deductibleBox303 ?? "",
            amt(l.withholding), amt(l.total)]);
    }
    rows.push([]);
    const totalsRowIdx = rows.length;
    rows.push(["", "", "", "", "", "", "", "", "TOTAL", "", "", "", amt(reg.totalBase), "", amt(reg.totalVat),
        amt(reg.totalDeductibleVat), amt(reg.totalReverseChargeVat), "", "", "", ""]);
    rows.push(...avisosRows(reg.avisos));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{wch: 8}, {wch: 7}, {wch: 16}, {wch: 11}, {wch: 8}, {wch: 11}, {wch: 36}, {wch: 13}, {wch: 36},
        {wch: 11}, {wch: 9}, {wch: 9}, {wch: 13}, {wch: 8}, {wch: 13}, {wch: 13}, {wch: 13}, {wch: 30}, {wch: 34},
        {wch: 10}, {wch: 13}];
    bold(ws, 2, header.length);
    bold(ws, totalsRowIdx, header.length);
    XLSX.utils.book_append_sheet(wb, ws, `Recibidas ${reg.quarter}T`);
    download(wb, `libro-iva-recibidas-${reg.year}-${reg.quarter}T.xlsx`);
}

// ---- Modelos tributarios (#14733): lo que se ve en el diálogo, en una hoja ----

function modelWorkbook(sheet: string, filename: string, rows: Row[], boldRows: number[], cols: number[], avisos?: string[]) {
    const all = [...rows, ...avisosRows(avisos)];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(all);
    ws["!cols"] = cols.map(wch => ({wch}));
    boldRows.forEach(r => bold(ws, r, cols.length));
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    download(wb, filename);
}

export function exportModelo303Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 303 — IVA ${m.quarter}T ${m.year}`],
        [],
        ["IVA devengado", "Casillas", "Base imponible", "Cuota"],
        ["Régimen general 4 %", "01 / 03", amt(m.base01), amt(m.cuota03)],
        ["Régimen general 10 %", "04 / 06", amt(m.base04), amt(m.cuota06)],
        ["Régimen general 21 %", "07 / 09", amt(m.base07), amt(m.cuota09)],
        ["Adquisiciones intracomunitarias", "10 / 11", amt(m.base10), amt(m.cuota11)],
        ["Otras operaciones con inversión del sujeto pasivo", "12 / 13", amt(m.base12), amt(m.cuota13)],
        ["Modificación de bases y cuotas", "14 / 15", amt(m.base14), amt(m.cuota15)],
        ["Total cuota devengada", "27", "", amt(m.total27)],
        [],
        ["IVA deducible", "Casillas", "Base imponible", "Cuota"],
        ["Operaciones interiores corrientes", "28 / 29", amt(m.base28), amt(m.cuota29)],
        ["Adquisiciones intracomunitarias de bienes y servicios corrientes", "36 / 37", amt(m.base36), amt(m.cuota37)],
        ["Rectificación de deducciones", "40 / 41", amt(m.base40), amt(m.cuota41)],
        ["Total a deducir", "45", "", amt(m.total45)],
        [],
        ["Resultado", "46", "", amt(m.resultado46)],
    ];
    modelWorkbook(`303 ${m.quarter}T`, `modelo-303-${m.year}-${m.quarter}T.xlsx`, rows, [2, 9, 11, 15, 17], [56, 10, 16, 16], m.avisos);
}

export function exportModelo111Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 111 — Retenciones ${m.quarter}T ${m.year}`],
        [],
        ["Concepto", "Casillas", "Perceptores", "Importe", "Retenciones"],
        ["Rendimientos del trabajo (dinerarios)", "01-03", m.perceptores01, amt(m.importe02), amt(m.retenciones03)],
        ["Actividades económicas (dinerarios)", "07-09", m.perceptores07, amt(m.importe08), amt(m.retenciones09)],
        ["Total liquidación", "28", "", "", amt(m.total28)],
        ["A deducir (complementaria)", "29", "", "", amt(m.deducir29)],
        ["Resultado a ingresar", "30", "", "", amt(m.resultado30)],
    ];
    modelWorkbook(`111 ${m.quarter}T`, `modelo-111-${m.year}-${m.quarter}T.xlsx`, rows, [2, 7], [40, 10, 12, 16, 16], m.avisos);
}

export function exportModelo190Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 190 — Resumen anual de retenciones ${m.year}`],
        [`Percepciones ${amt(m.totalPercepciones)} · Retenciones ${amt(m.totalRetenciones)} · Registros ${m.numPerceptores ?? ""} · Suma de los 111 ${amt(m.retenciones111)}`],
        [],
        ["Clave", "Descripción", "Perceptores", "Percepciones", "Retenciones"],
    ];
    const boldRows = [3];
    for (const c of m.claves ?? []) {
        rows.push([`${c.clave ?? ""}${c.subclave ? ` ${c.subclave}` : ""}`, c.descripcion ?? "", c.perceptores, amt(c.percepcionIntegra), amt(c.retenciones)]);
    }
    rows.push([]);
    boldRows.push(rows.length);
    rows.push(["NIF", "Nombre", "Clave", "Percepción íntegra", "Retenciones", "Documentos"]);
    for (const p of m.perceptores ?? []) {
        rows.push([p.nif ?? "", p.nombre ?? "", `${p.clave ?? ""}${p.subclave ? ` ${p.subclave}` : ""}`, amt(p.percepcionIntegra),
            amt(p.retenciones), p.numDocumentos]);
    }
    modelWorkbook("190", `modelo-190-${m.year}.xlsx`, rows, boldRows, [14, 40, 12, 16, 16, 11], m.avisos);
}

export function exportModelo347Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 347 — Operaciones con terceros ${m.year}`],
        [`Umbral ${amt(Number(m.threshold))} · Adquisiciones (A) ${amt(m.totalAdquisiciones)} · Entregas (B) ${amt(m.totalEntregas)} · Total ${amt(m.total)}. Importes con IVA.`],
        [],
        ["NIF", "Nombre", "Clave", "1T", "2T", "3T", "4T", "Anual"],
    ];
    for (const l of m.lines ?? []) {
        rows.push([l.nif ?? "Sin NIF", l.nombre ?? "", l.clave ?? "", amt(l.importe1T), amt(l.importe2T), amt(l.importe3T),
            amt(l.importe4T), amt(l.importeAnual)]);
    }
    const avisos = [...(m.errores ?? []), ...(m.avisos ?? [])];
    modelWorkbook("347", `modelo-347-${m.year}.xlsx`, rows, [3], [13, 40, 6, 14, 14, 14, 14, 15], avisos);
}

const CLAVE_349: Record<string, string> = {
    E: "Entrega intracomunitaria", A: "Adquisición intracomunitaria", S: "Servicio prestado", I: "Servicio recibido",
};

export function exportModelo349Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 349 — Operaciones intracomunitarias ${m.quarter}T ${m.year}`],
        [`Entregas (E) ${amt(m.totalEntregas)} · Adquisiciones (A) ${amt(m.totalAdquisiciones)}`],
        [],
        ["NIF intracomunitario", "Nombre", "Clave", "Descripción", "Base imponible"],
    ];
    for (const l of m.lines ?? []) {
        rows.push([l.vatEu ?? "", l.nombre ?? "", l.claveOperacion ?? "", CLAVE_349[l.claveOperacion] ?? "", amt(l.baseImponible)]);
    }
    modelWorkbook(`349 ${m.quarter}T`, `modelo-349-${m.year}-${m.quarter}T.xlsx`, rows, [3], [18, 40, 6, 26, 15], m.avisos);
}

export function exportModelo390Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 390 — Resumen anual de IVA ${m.year}`],
        [],
        ["Concepto", "Importe"],
        ["Base imponible emitidas", amt(m.baseEmitidas)],
        ["Cuota IVA emitidas", amt(m.vatEmitidas)],
        ["Base imponible recibidas", amt(m.baseRecibidas)],
        ["Cuota IVA recibidas (deducible)", amt(m.vatRecibidas)],
        ["Resultado neto (a ingresar / a devolver)", amt(m.netResult)],
    ];
    modelWorkbook("390", `modelo-390-${m.year}.xlsx`, rows, [2, 7], [44, 16], m.avisos);
}

export function exportModelo115Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 115 — Retenciones de alquileres ${m.quarter}T ${m.year}`],
        [],
        ["Concepto", "Importe"],
        ["Arrendadores (perceptores)", m.numArrendadores],
        ["Base imponible (alquileres)", amt(m.baseImponible)],
        ["Retención", amt(m.retencion)],
        ["A ingresar", amt(m.aIngresar)],
    ];
    modelWorkbook(`115 ${m.quarter}T`, `modelo-115-${m.year}-${m.quarter}T.xlsx`, rows, [2, 6], [36, 16], m.avisos);
}

export function exportModelo180Xls(m: any) {
    const rows: Row[] = [
        [`Modelo 180 — Resumen anual de alquileres ${m.year}`],
        [`Base ${amt(m.totalBase)} · Retención ${amt(m.totalRetencion)} · Arrendadores ${(m.lines ?? []).length}`],
        [],
        ["NIF", "Nombre", "Base imponible", "Retención", "Facturas"],
    ];
    for (const l of m.lines ?? []) {
        rows.push([l.nif ?? "", l.nombre ?? "", amt(l.baseImponible), amt(l.retencion), l.numFacturas]);
    }
    modelWorkbook("180", `modelo-180-${m.year}.xlsx`, rows, [3], [13, 40, 16, 16, 10], m.avisos);
}
